/**
 * IntakePlannenPage — Calendly embed voor goedgekeurde partners.
 *
 * Fetcht de Calendly URL uit /api/platform/intake/calendly (admin-configureerbaar).
 * Embed via iframe. Toon nette empty-state als calendly_enabled=false.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Calendar, ExternalLink, AlertCircle, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function IntakePlannenPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Niet ingelogd');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/platform/intake/calendly`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInfo(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Kon intake-info niet laden');
      }
      setLoading(false);
    })();
  }, [token]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
        <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />
        <p>Intake-pagina laden…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, color: 'white' }} data-testid="intake-error">
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 14, padding: 24, maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <AlertCircle size={36} color="#ef4444" style={{ marginBottom: 12 }} />
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Geen toegang tot intake</h2>
          <p style={{ color: '#fca5a5', fontSize: 14, lineHeight: 1.6 }}>{error}</p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 16 }}>
            Intake-gesprekken zijn alleen beschikbaar voor goedgekeurde partners.
          </p>
        </div>
      </div>
    );
  }

  if (!info?.calendly_enabled || !info?.calendly_url) {
    return (
      <div style={{ padding: 40, color: 'white' }} data-testid="intake-disabled">
        <div style={{ background: 'rgba(212,160,23,0.1)', border: '1px solid rgba(212,160,23,0.4)', borderRadius: 14, padding: 24, maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <Calendar size={36} color="#D4A017" style={{ marginBottom: 12 }} />
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Intake tijdelijk niet beschikbaar</h2>
          <p style={{ color: '#fde68a', fontSize: 14, lineHeight: 1.6 }}>
            Online intake plannen is op dit moment uitgeschakeld. Neem rechtstreeks contact op via{' '}
            <a href="mailto:info@imperiaai.nl" style={{ color: '#D4A017' }}>info@imperiaai.nl</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, color: 'white' }} data-testid="intake-page">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>Intake plannen</h1>
        <p style={{ color: '#94a3b8', marginTop: 4, fontSize: 14 }}>
          {info.calendly_label || 'Plan een korte intake — we leggen u graag alles uit.'}
        </p>
        {info.calendly_source === 'parent_chain' && info.pm_owner && (
          <div style={{ marginTop: 12, background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#c7d2fe', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: '#6366f1', color: 'white', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Uw PM</span>
            U plant uw intake met <strong>{info.pm_owner.name}</strong> ({info.pm_owner.email})
          </div>
        )}
      </div>

      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 16 }}>
        <iframe
          src={info.calendly_url}
          title="Calendly Intake"
          data-testid="intake-calendly-iframe"
          width="100%"
          height={760}
          frameBorder="0"
          style={{ borderRadius: 10, background: 'white' }}
        />
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', background: 'rgba(15,23,42,0.4)', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#cbd5e1' }}>
        <span>Werkt het embed-scherm niet? Open Calendly in een nieuw tabblad.</span>
        <a
          href={info.calendly_url}
          target="_blank"
          rel="noreferrer"
          data-testid="intake-external-link"
          style={{ color: '#D4A017', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ExternalLink size={14} /> Open in nieuw tabblad
        </a>
      </div>

      <div style={{ marginTop: 20, background: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)', borderRadius: 12, padding: 16, fontSize: 13, color: '#fde68a', display: 'flex', gap: 12 }}>
        <Mail size={18} color="#D4A017" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong>Wat gebeurt er na het plannen?</strong>
          <p style={{ marginTop: 4, marginBottom: 0, color: '#fde68a99', lineHeight: 1.6 }}>
            U krijgt een bevestigingsmail met een Google Meet-link. Tijdens het gesprek bespreken we uw partner-traject, uw materialen
            en het uitbetalingsproces.
          </p>
        </div>
      </div>
    </div>
  );
}
